/*  Proposta C — Ticket List
    Stress test: tabella densa con filtri, bulk actions, dati operativi reali.
*/

import React, { useState } from 'react';
import { useV2Fonts } from './_shared';
import { CShell, C, CBadge, CCard } from './_cShell';
import {
  Filter, ChevronDown, ArrowUpDown, MoreHorizontal, AlertOctagon,
  Clock, CheckCircle2, Image as ImageIcon, MapPin
} from 'lucide-react';

const TICKETS = [
  { id: '#3412', title: 'Climatizzatore in tilt — rumore + niente raffreddamento', hotel: 'GAL', room: '405', priority: 'crit', tech: 'M. Rossi', status: 'in_progress', age: '12m', photos: 2 },
  { id: '#3411', title: 'Cassaforte bloccata, ospite in attesa', hotel: 'GAL', room: '301', priority: 'crit', tech: 'L. Bianchi', status: 'assigned', age: '34m', photos: 0 },
  { id: '#3408', title: 'Perdita lavandino lobby — pavimento bagnato', hotel: 'ARC', room: 'Hall', priority: 'high', tech: 'M. Rossi', status: 'in_progress', age: '48m', photos: 3 },
  { id: '#3401', title: 'Ascensore B fermo piano 4', hotel: 'MNZ', room: 'Asc-B', priority: 'crit', tech: 'Ext. Otis', status: 'waiting_ext', age: '1h 12m', photos: 1 },
  { id: '#3395', title: 'Macchia muffa soffitto bagno', hotel: 'GAL', room: '402', priority: 'med', tech: 'D. Conti', status: 'open', age: '1h 50m', photos: 4 },
  { id: '#3389', title: 'TV non accende, telecomando OK', hotel: 'GAL', room: '208', priority: 'med', tech: 'A. Costa', status: 'assigned', age: '2h 30m', photos: 0 },
  { id: '#3387', title: 'Cardiofrequenzimetro palestra — schermo nero', hotel: 'ARC', room: 'Gym', priority: 'low', tech: '—', status: 'open', age: '2h 45m', photos: 0 },
  { id: '#3382', title: 'Frigobar non raffredda, prodotti caldi', hotel: 'GAL', room: '317', priority: 'med', tech: 'D. Conti', status: 'assigned', age: '3h 05m', photos: 1 },
  { id: '#3380', title: 'Tapparella elettrica blocca a metà', hotel: 'KIR', room: '521', priority: 'low', tech: '—', status: 'open', age: '3h 20m', photos: 0 },
  { id: '#3377', title: 'Acqua marrone dal lavabo (3 camere stesso piano)', hotel: 'MNZ', room: '6° p.', priority: 'high', tech: 'M. Rossi', status: 'in_progress', age: '4h', photos: 2 },
  { id: '#3372', title: 'Lampadina bagno fulminata', hotel: 'GAL', room: '208', priority: 'low', tech: 'A. Costa', status: 'done', age: 'ieri', photos: 0 },
  { id: '#3365', title: 'Cassaforte bloccata', hotel: 'GAL', room: '301', priority: 'med', tech: 'L. Bianchi', status: 'done', age: '2g fa', photos: 1 },
];

const STATUS = {
  open: { label: 'OPEN', color: '#94A3B8' },
  assigned: { label: 'ASSIGNED', color: '#3B82F6' },
  in_progress: { label: 'IN_PROGRESS', color: '#F59E0B' },
  waiting_ext: { label: 'WAITING_EXT', color: '#A855F7' },
  done: { label: 'DONE', color: '#10B981' },
};

const PRIORITY = {
  crit: { label: 'CRIT', color: C.danger },
  high: { label: 'HIGH', color: C.warning },
  med: { label: 'MED', color: C.muted },
  low: { label: 'LOW', color: C.mutedDim },
};

const FILTERS = [
  { label: 'Status', value: 'All' },
  { label: 'Priority', value: 'All' },
  { label: 'Hotel', value: 'All' },
  { label: 'Tech', value: 'All' },
  { label: 'Period', value: 'Last 7d' },
];

const ProposalCTickets = () => {
  useV2Fonts();
  const [selected, setSelected] = useState(new Set(['#3412']));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  return (
    <CShell hotel="Multi-Hotel View">
      {/* Page header */}
      <div className="mb-5 flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
            MODULE_02 · TICKET_LIST
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: C.fontDisplay }}>Tickets</h1>
          <p className="text-xs mt-1" style={{ color: C.muted, fontFamily: C.fontMono }}>
            {TICKETS.length} total · {TICKETS.filter(t => t.status !== 'done').length} active · 4 critical
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <CBadge color={C.accent}>{selected.size} SELECTED</CBadge>
              <button
                className="px-3 py-1.5 text-xs rounded-md"
                style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: C.fontMono }}
              >
                ASSIGN
              </button>
              <button
                className="px-3 py-1.5 text-xs rounded-md"
                style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: C.fontMono }}
              >
                MOVE_STATUS
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter strip */}
      <div className="flex items-center gap-2 mb-3">
        <Filter className="w-3.5 h-3.5" style={{ color: C.muted }} />
        {FILTERS.map((f) => (
          <button
            key={f.label}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs"
            style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: C.fontMono }}
          >
            <span style={{ color: C.mutedDim }}>{f.label}:</span>
            <span>{f.value}</span>
            <ChevronDown className="w-3 h-3" style={{ color: C.mutedDim }} />
          </button>
        ))}
        <div className="flex-1" />
        <CBadge color={C.success}>● REAL-TIME</CBadge>
      </div>

      {/* Aggregate cards */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        {[
          { l: 'OPEN', v: 3, c: C.muted, icon: AlertOctagon },
          { l: 'ASSIGNED', v: 4, c: C.accent, icon: Clock },
          { l: 'IN_PROGRESS', v: 3, c: C.warning, icon: Clock },
          { l: 'WAITING_EXT', v: 1, c: '#A855F7', icon: Clock },
          { l: 'DONE_TODAY', v: 2, c: C.success, icon: CheckCircle2 },
        ].map((k) => (
          <div
            key={k.l}
            className="rounded-md p-3 relative overflow-hidden"
            style={{ background: C.surface, border: `1px solid ${C.border}` }}
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
                {k.l}
              </div>
              <k.icon className="w-3.5 h-3.5" style={{ color: k.c }} />
            </div>
            <div
              className="text-3xl font-bold mt-1 tabular-nums"
              style={{ color: k.c, fontFamily: C.fontDisplay, textShadow: `0 0 24px ${k.c}33` }}
            >
              {k.v}
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-md overflow-hidden" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
        <div
          className="grid items-center px-4 py-2 text-[10px] tracking-widest"
          style={{
            gridTemplateColumns: '24px 70px 60px 1fr 60px 60px 110px 110px 70px 24px',
            color: C.mutedDim,
            fontFamily: C.fontMono,
            borderBottom: `1px solid ${C.border}`,
            background: C.surface2,
          }}
        >
          <span>{' '}</span>
          <span className="flex items-center gap-1">ID <ArrowUpDown className="w-2.5 h-2.5" /></span>
          <span>PRI</span>
          <span>TITLE</span>
          <span>HOTEL</span>
          <span>ROOM</span>
          <span>ASSIGNED</span>
          <span>STATUS</span>
          <span className="text-right">AGE</span>
          <span></span>
        </div>

        {TICKETS.map((t, i) => {
          const isSelected = selected.has(t.id);
          const isLast = i === TICKETS.length - 1;
          const s = STATUS[t.status];
          const p = PRIORITY[t.priority];
          return (
            <div
              key={t.id}
              className="grid items-center px-4 py-2.5 text-xs cursor-pointer"
              style={{
                gridTemplateColumns: '24px 70px 60px 1fr 60px 60px 110px 110px 70px 24px',
                background: isSelected ? 'rgba(59,130,246,0.06)' : 'transparent',
                borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
                color: t.status === 'done' ? C.muted : C.text,
              }}
              onClick={() => toggle(t.id)}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggle(t.id)}
                className="w-3 h-3 accent-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
              <span className="tabular-nums" style={{ fontFamily: C.fontMono, color: C.muted }}>{t.id}</span>
              <CBadge color={p.color}>{p.label}</CBadge>
              <span className="flex items-center gap-2 truncate">
                <span className="truncate">{t.title}</span>
                {t.photos > 0 && (
                  <span className="flex items-center gap-0.5 text-[10px]" style={{ color: C.mutedDim }}>
                    <ImageIcon className="w-3 h-3" /> {t.photos}
                  </span>
                )}
              </span>
              <span style={{ fontFamily: C.fontMono, color: C.muted }}>{t.hotel}</span>
              <span className="flex items-center gap-1" style={{ fontFamily: C.fontMono, color: C.muted }}>
                <MapPin className="w-3 h-3" /> {t.room}
              </span>
              <span style={{ fontFamily: C.fontMono, color: t.tech === '—' ? C.mutedDim : C.text }}>{t.tech}</span>
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color, boxShadow: `0 0 6px ${s.color}` }} />
                <span style={{ color: s.color, fontFamily: C.fontMono }}>{s.label}</span>
              </span>
              <span className="text-right tabular-nums" style={{ color: C.muted, fontFamily: C.fontMono }}>{t.age}</span>
              <MoreHorizontal className="w-3.5 h-3.5" style={{ color: C.mutedDim }} />
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-3 flex items-center justify-between text-xs" style={{ color: C.muted, fontFamily: C.fontMono }}>
        <span>Showing 1-{TICKETS.length} of 247 tickets</span>
        <div className="flex items-center gap-2">
          <button className="px-2 py-1 rounded" style={{ background: C.surface, border: `1px solid ${C.border}` }}>‹ PREV</button>
          <span className="px-2">PAGE 1 / 21</span>
          <button className="px-2 py-1 rounded" style={{ background: C.surface, border: `1px solid ${C.border}` }}>NEXT ›</button>
        </div>
      </div>
    </CShell>
  );
};

export default ProposalCTickets;
